import React, { useState } from "react";
import {
  useGetSchedulersQuery,
  useDeleteScheduleMutation,
  useUpdateSchedulerStatusMutation,
  ScheduleType,
} from "../store/slices/settingsSlice";
import MyPagination from "../Common/MyPagination";
import ScheduleSettings from "./ScheduleSettings";
import { toast } from "react-toastify";
import ScheduleEdit from "./ScheduleEdit";
import MyModal from "../Common/MyModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateLeft, faTrash } from "@fortawesome/free-solid-svg-icons";
import { Form } from "react-bootstrap";
import { useConfirmDialog } from "../Builder/Components/ConfirmDialogContext";
import { Link } from "react-router-dom";

export default function SchedulersList({
  broadcasterUuid,
  projectUuid,
}: {
  broadcasterUuid?: string;
  projectUuid?: string;
}) {
  const confirmDialog = useConfirmDialog();
  const [pagination, setPagination] = useState({ page: 0, perPage: 10 });
  const [schedulerFilters, setSchedulerFilters] = useState<{
    project_uuid?: string;
    broadcaster_uuid?: string;
    is_active?: boolean;
    search?: string;
  }>({ broadcaster_uuid: broadcasterUuid, project_uuid: projectUuid });

  const { data: schedulersData, refetch } = useGetSchedulersQuery({
    page: pagination.page,
    per_page: pagination.perPage,
    ...schedulerFilters,
  });
  const [deleteSchedule] = useDeleteScheduleMutation();
  const [updateSchedulerStatus] = useUpdateSchedulerStatusMutation();
  const [editedSchedule, setEditedSchedule] = useState<
    ScheduleType | undefined
  >(undefined);

  return (
    <div className="p-3 rounded-3 bg-white my-3">
      {!schedulersData?.schedules?.length ? (
        <div className="alert alert-warning">No schedulers found.</div>
      ) : (
        <table className="table table-bg-transparent">
          <thead>
            <tr>
              <th>Label</th>
              {!projectUuid && !broadcasterUuid ? <th>Target</th> : null}
              <th>Frequency</th>
              <th>Active</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Max Runs</th>
              <th>Next Run</th>
              <th className="">
                <button
                  type="button"
                  className="btn btn-sm border-0"
                  onClick={() => {
                    refetch();
                  }}
                >
                  <FontAwesomeIcon icon={faRotateLeft} />
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {schedulersData.schedules.map((scheduler) => (
              <tr key={scheduler.Uuid}>
                <td>
                  <MyModal
                    label={scheduler.Label}
                    heading="Edit Schedule"
                    size="lg"
                    buttonClassName="text-start btn-link"
                    show={editedSchedule?.Uuid === scheduler.Uuid}
                    onHide={() => {
                      setEditedSchedule(undefined);
                    }}
                  >
                    {({ handleClose }) => (
                      <ScheduleEdit
                        uuid={scheduler.Uuid}
                        broadcasterUuid={scheduler.BroadcasterUuid}
                        projectUuid={scheduler.ProjectUuid}
                        onClose={handleClose}
                        onSave={handleClose}
                      />
                    )}
                  </MyModal>
                </td>
                {!projectUuid && !broadcasterUuid && (
                  <td>
                    {scheduler.Broadcaster && !broadcasterUuid ? (
                      <>
                        <Link
                          to={`/library/broadcast/${scheduler.BroadcasterUuid}/messages`}
                          title={scheduler.Broadcaster.Description}
                        >
                          {scheduler.Broadcaster.Label}
                        </Link>
                        <span className="badge bg-secondary ms-2">
                          Broadcast
                        </span>
                      </>
                    ) : (
                      ""
                    )}
                    {scheduler.Project && !projectUuid ? (
                      <>
                        <Link
                          to={`/library/logic/builder/${scheduler.Project.ID}`}
                          title={scheduler.Project.Description}
                        >
                          {scheduler.Project.Label}
                        </Link>
                        <span className="badge bg-secondary ms-2">
                          Logic component
                        </span>
                      </>
                    ) : (
                      ""
                    )}
                  </td>
                )}
                <td>
                  {scheduler.MaxRuns === 1 ? (
                    <span className="badge bg-secondary text-capitalize">
                      One-time
                    </span>
                  ) : (
                    <>
                      <span className="badge bg-secondary text-capitalize">
                        {scheduler.FrequencyKind === "fixed"
                          ? "Daily"
                          : scheduler.FrequencyKind}
                      </span>
                      {scheduler.FrequencyKind === "interval" && (
                        <span className="ms-1 text-secondary small">
                          {scheduler.IntervalValue} {scheduler.IntervalUnit}
                        </span>
                      )}
                    </>
                  )}
                </td>
                <td>
                  <Form.Check // prettier-ignore
                    type="switch"
                    id={`schedule-is-active-switch-${scheduler.Uuid}`}
                    checked={scheduler.IsActive}
                    onChange={async () => {
                      const result = await updateSchedulerStatus({
                        uuid: scheduler.Uuid || "",
                        isActive: !scheduler.IsActive,
                      });
                      if ("error" in result) {
                        console.log(result.error);

                        if (
                          !(await confirmDialog(
                            "Could not update scheduler status",
                            `
                              We could not update the scheduler status due to
                              the following error:
                              <div class="alert alert-secondary my-2">
                                ${result.error.data.message}
                              </div>
                               Would you like to edit the schedule?
                            `
                          ))
                        ) {
                          return;
                        }
                        setEditedSchedule(scheduler);
                      } else {
                        toast.success("Scheduler status updated successfully");
                      }
                    }}
                  />
                </td>
                <td>{scheduler.StartDate?.slice(0, 10) || "—"}</td>
                <td>{scheduler.EndDate?.slice(0, 10) || "—"}</td>
                <td>
                  {!scheduler.MaxRuns
                    ? scheduler.EndDate
                      ? "Run until " + scheduler.EndDate?.slice(0, 10)
                      : "Run forever"
                    : scheduler.MaxRuns}
                </td>
                <td>
                  {scheduler.NextRun && scheduler.IsActive
                    ? (() => {
                        const nextRunDate = new Date(scheduler.NextRun);
                        if (isNaN(nextRunDate.getTime())) {
                          return "Invalid date";
                        }
                        return `${nextRunDate.getFullYear()}-${(
                          nextRunDate.getMonth() + 1
                        )
                          .toString()
                          .padStart(2, "0")}-${nextRunDate
                          .getDate()
                          .toString()
                          .padStart(2, "0")} ${nextRunDate
                          .getHours()
                          .toString()
                          .padStart(2, "0")}:${nextRunDate
                          .getMinutes()
                          .toString()
                          .padStart(2, "0")}`;
                      })()
                    : "—"}
                </td>
                <td>
                  {scheduler.Uuid ? (
                    <button
                      className="btn btn-sm border-0 btn-outline-danger"
                      onClick={async () => {
                        if (
                          !(await confirmDialog(
                            "Delete schedule?",
                            "Are you sure you want to delete this schedule? This action cannot be undone."
                          ))
                        ) {
                          return;
                        }
                        const result = await deleteSchedule({
                          uuid: scheduler.Uuid || "",
                        });
                        if ("error" in result) {
                          toast.error("Failed to delete schedule");
                        } else {
                          toast.success("Schedule deleted successfully");
                        }
                      }}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <MyPagination
        total={schedulersData?.total}
        onPaginate={(page: number, perPage: number) =>
          setPagination({ page, perPage })
        }
      />
    </div>
  );
}
